import { useState, useEffect } from "react";
import { toast } from "sonner";
import { UserPlus, Contact, Send, Trash2, Plus, Copy, Check, Share2, Info, Users2, BadgeCheck, Clock3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, APP_NAME, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const supportsContacts = () => !!(navigator.contacts && navigator.contacts.select && window.ContactsManager);

function sanitizePhone(raw) {
  let d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 10 || d.length === 11) d = "55" + d; // BR local -> add country code
  return d;
}

export default function Invite() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]); // {name, phone}
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [copied, setCopied] = useState(false);
  const [invites, setInvites] = useState([]);

  useEffect(() => {
    api.get("/my-invites").then(({ data }) => setInvites(data)).catch(() => {});
  }, []);

  const inviteLink = `${window.location.origin}/register?ref=${user?.id || ""}`;
  const inviter = user?.stage_name || "Um amigo";

  const buildMessage = (toName) => {
    const hi = toName ? `Olá, ${toName}! ` : "Olá! ";
    return [
      `${hi}🎵`,
      `${inviter} está te convidando para o *${APP_NAME}* — a plataforma que organiza músicos freelancers: vagas de shows, cachês via Pix, agenda inteligente e repertório.`,
      "",
      "Cadastre-se aqui:",
      inviteLink,
    ].join("\n");
  };

  const waLink = (c) => {
    const p = sanitizePhone(c.phone);
    const text = encodeURIComponent(buildMessage(c.name));
    return p ? `https://wa.me/${p}?text=${text}` : `https://wa.me/?text=${text}`;
  };

  const pickFromContacts = async () => {
    if (!supportsContacts()) return toast.error("Seu aparelho/navegador não permite ler contatos. Use o Chrome no Android ou adicione manualmente.");
    try {
      const props = ["name", "tel"];
      const selected = await navigator.contacts.select(props, { multiple: true });
      const mapped = selected
        .map((c) => ({ name: (c.name && c.name[0]) || "", phone: (c.tel && c.tel[0]) || "" }))
        .filter((c) => c.phone);
      if (mapped.length === 0) return toast.error("Nenhum contato com telefone selecionado.");
      setContacts((prev) => [...prev, ...mapped]);
      toast.success(`${mapped.length} contato(s) adicionado(s).`);
    } catch {
      toast.error("Seleção de contatos cancelada.");
    }
  };

  const addManual = () => {
    if (!phone.trim()) return toast.error("Informe um número de telefone.");
    setContacts((prev) => [...prev, { name: name.trim(), phone: phone.trim() }]);
    setName(""); setPhone("");
  };
  const removeContact = (i) => setContacts((prev) => prev.filter((_, x) => x !== i));

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Link copiado!"); }
    catch { toast.error("Não foi possível copiar."); }
  };

  const inp = "bg-[#09090B] border-white/10 rounded-sm";

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="invite-page">
      <div>
        <h1 className="font-head text-4xl font-bold tracking-tight flex items-center gap-3"><UserPlus className="h-8 w-8 text-[#00F0FF]" />Convidar músicos</h1>
        <p className="text-zinc-500 mt-1">Chame outros músicos e contratantes para o {APP_NAME}. Envie o convite direto pelo WhatsApp.</p>
      </div>

      {/* Personal invite link */}
      <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-6 space-y-3">
        <h2 className="font-head text-xl font-semibold">Seu link de convite</h2>
        <div className="flex gap-2">
          <Input data-testid="invite-link" readOnly value={inviteLink} className={`${inp} font-mono text-xs`} />
          <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline" className="rounded-sm bg-transparent border-white/10 gap-1 shrink-0">{copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}{copied ? "Copiado" : "Copiar"}</Button>
        </div>
        <a data-testid="share-generic-wa" href={`https://wa.me/?text=${encodeURIComponent(buildMessage(""))}`} target="_blank" rel="noreferrer">
          <Button className="bg-[#25D366] hover:bg-[#1EBE5A] text-black rounded-sm gap-2"><Share2 className="h-4 w-4" />Compartilhar no WhatsApp</Button>
        </a>
      </div>

      {/* Meus Convites (quem se cadastrou pelo seu link) */}
      <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-6 space-y-4" data-testid="my-invites-card">
        <div className="flex items-center gap-2">
          <Users2 className="h-5 w-5 text-[#00F0FF]" />
          <h2 className="font-head text-xl font-semibold">Meus convites</h2>
          <span className="text-xs text-zinc-500">({invites.length} cadastrado{invites.length === 1 ? "" : "s"})</span>
        </div>
        {invites.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="my-invites-empty">Ninguém se cadastrou pelo seu link ainda. Compartilhe e acompanhe aqui quem entrar.</p>
        ) : (
          <div className="space-y-2">
            {invites.map((m) => (
              <div key={m.id} data-testid={`invited-user-${m.id}`} className="flex items-center gap-3 bg-[#09090B] border border-white/10 rounded-sm px-3 py-2">
                <div className="h-9 w-9 rounded-full bg-[#1C1C24] overflow-hidden shrink-0 flex items-center justify-center text-xs text-zinc-400">
                  {m.photo ? <img src={m.photo} alt={m.stage_name} className="h-full w-full object-cover" /> : (m.stage_name?.[0] || "?").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{m.stage_name || "Novo músico"}</div>
                  <div className="text-xs text-zinc-500 truncate">{[m.city, m.state].filter(Boolean).join(" · ") || "Cadastrou-se"}{m.created_at ? ` · ${formatDate(m.created_at)}` : ""}</div>
                </div>
                {m.email_verified
                  ? <span className="text-[11px] px-2 py-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 flex items-center gap-1 shrink-0"><BadgeCheck className="h-3.5 w-3.5" />Cadastrou</span>
                  : <span className="text-[11px] px-2 py-1 rounded-sm border border-amber-500/30 bg-amber-500/10 text-amber-400 flex items-center gap-1 shrink-0"><Clock3 className="h-3.5 w-3.5" />Confirmando e-mail</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contacts */}
      <div className="bg-[#121217] border border-white/[0.06] rounded-sm p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-head text-xl font-semibold">Convidar dos contatos</h2>
          <Button data-testid="pick-contacts-btn" onClick={pickFromContacts} variant="outline" className="rounded-sm bg-transparent border-[#00F0FF]/30 text-[#00F0FF] gap-2"><Contact className="h-4 w-4" />Escolher dos contatos</Button>
        </div>
        {!supportsContacts() && (
          <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-sm p-3" data-testid="contacts-unsupported">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>A leitura da agenda só funciona no Chrome/Edge do Android. Neste aparelho, adicione os números manualmente abaixo.</span>
          </div>
        )}

        {/* Manual add */}
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="manual-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome (opcional)" className={`${inp} flex-1 min-w-[140px]`} />
          <Input data-testid="manual-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefone com DDD" className={`${inp} flex-1 min-w-[140px]`} inputMode="tel" />
          <Button data-testid="add-manual-btn" onClick={addManual} variant="outline" className="rounded-sm bg-transparent border-white/10 gap-1"><Plus className="h-4 w-4" />Adicionar</Button>
        </div>

        {/* List */}
        {contacts.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhum contato adicionado ainda.</p>
        ) : (
          <div className="space-y-2">
            {contacts.map((c, i) => (
              <div key={i} data-testid={`invite-contact-${i}`} className="flex items-center gap-3 bg-[#09090B] border border-white/10 rounded-sm px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name || "Sem nome"}</div>
                  <div className="text-xs text-zinc-500 truncate">{c.phone}</div>
                </div>
                <a data-testid={`send-wa-${i}`} href={waLink(c)} target="_blank" rel="noreferrer">
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#1EBE5A] text-black rounded-sm gap-1 h-8"><Send className="h-3.5 w-3.5" />Convidar</Button>
                </a>
                <Button size="sm" variant="ghost" data-testid={`remove-contact-${i}`} onClick={() => removeContact(i)} className="text-red-400 rounded-sm h-8 px-2"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
